// Implements: F-IDN-009, F-IDN-026, F-UIX-010
/**
 * `/invitacion/:token` (Default P3-1): the page a copied invitation link opens, so the link works
 * without e-mail (P2). It accepts the invitation with the person's name and password
 * (`POST /auth/invitations/accept`, sent once); on success the server has set the session cookie
 * and `onAccepted` takes the person in.
 *
 * Only a dead link shows "Esta invitación ya no es válida" with the way to sign in: a token the
 * server does not know (`401`, a rotated or invented link), an invitation already used or revoked
 * (any `409` except the one naming the taken address) and an expired one (`422` naming the token as
 * `expired`). Every other refusal (a password the policy refuses, an address that already has an
 * account: `409` with `{field: "email", reason: "taken"}`; a suspended organization, owner
 * 2026-10-26) keeps the form and says the server's reason.
 */

import { useState } from "react";
import type { ReactElement } from "react";
import { Link } from "react-router";

import type { ApiError } from "../../../shared/api/ApiError";
import { asApiError } from "../../../shared/api/asApiError";
import { useTranslation } from "../../../shared/i18n";
import { buttonClassName } from "../../../shared/ui/atoms/Button";
import { EmptyState } from "../../../shared/ui/molecules/EmptyState";
import { PageHeader } from "../../../shared/ui/molecules/PageHeader";
import { InvitationAcceptForm } from "../components/InvitationAcceptForm";
import styles from "../components/Organization.module.css";
import { useAcceptInvitation } from "../hooks/useAcceptInvitation";
import { suspendedOrganizationRefusal } from "../model/invitationRefusal";
import { writeProblem } from "./PeopleContainer";

export const LOGIN_PATH = "/login";

/** Whether a refusal means the link itself no longer works. */
export function invitationGone(error: ApiError): boolean {
  if (error.status === 401) return true;
  if (error.status === 409) {
    if (suspendedOrganizationRefusal(error)) return false;
    return !(error.details?.field === "email" && error.details.reason === "taken");
  }
  return error.status === 422 && error.details?.field === "token" && error.details.reason === "expired";
}

export interface InvitationAcceptContainerProps {
  readonly token: string;
  readonly onAccepted: () => void;
}

export function InvitationAcceptContainer({ token, onAccepted }: InvitationAcceptContainerProps): ReactElement {
  const { t } = useTranslation();
  const accept = useAcceptInvitation();
  const [gone, setGone] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  if (gone) {
    return (
      <EmptyState
        icon="mail"
        title={t("organization.accept.invalid.title")}
        body={t("organization.accept.invalid.body")}
        action={
          <Link to={LOGIN_PATH} className={buttonClassName("secondary")}>
            {t("organization.accept.login")}
          </Link>
        }
      />
    );
  }

  return (
    <section className={styles.page}>
      <PageHeader title={t("organization.accept.title")} subtitle={t("organization.accept.intro")} />
      <InvitationAcceptForm
        pending={accept.isPending}
        problem={problem}
        onSubmit={(fullName, password) => {
          setProblem(null);
          accept.mutate(
            { token, full_name: fullName, password },
            {
              onSuccess: onAccepted,
              onError: (cause) => {
                const error = asApiError(cause);
                if (invitationGone(error)) setGone(true);
                else setProblem(writeProblem(error, t("common.state.error")));
              },
            },
          );
        }}
      />
    </section>
  );
}
